import type { ZonedTimeString } from "@poi-planner/shared";
import { daysBetweenDates, epochMs, parseZoned, zonedDate } from "./zonedTime";
import { timezoneCity } from "./timezoneDisplay";

export interface EventSpan {
  /** Elapsed minutes between the two instants. */
  minutes: number;
  /** End wall date minus start wall date, each in its own zone. */
  endDayOffset: number;
  /** End zone's UTC offset minus the start zone's, in minutes, at each end. */
  zoneShiftMinutes: number;
  startCity: string;
  endCity: string;
}

/**
 * What an event's start and end add up to. Flights are the case that
 * matters: departing 09:10 London and arriving 06:45 Tokyo the next day is
 * 13 h 35 m, which isn't obvious from the two wall times.
 */
export function describeEventSpan(
  start: ZonedTimeString,
  end: ZonedTimeString,
): EventSpan {
  const startZoned = parseZoned(start);
  const endZoned = parseZoned(end);
  return {
    minutes: Math.round((epochMs(end) - epochMs(start)) / 60_000),
    endDayOffset: daysBetweenDates(zonedDate(start), zonedDate(end)),
    zoneShiftMinutes: Math.round(
      (endZoned.offsetNanoseconds - startZoned.offsetNanoseconds) / 6e10,
    ),
    startCity: timezoneCity(startZoned.timeZoneId),
    endCity: timezoneCity(endZoned.timeZoneId),
  };
}

function formatHoursMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return `${minutes} m`;
  return minutes ? `${hours} h ${minutes} m` : `${hours} h`;
}

export function formatSpanDuration(minutes: number): string {
  return formatHoursMinutes(Math.max(0, minutes));
}

export function formatEndDayOffset(days: number): string | null {
  if (days <= 0) return null;
  return days === 1 ? "Ends the next day" : `Ends ${days} days later`;
}

/** "Tokyo is 8 h ahead of London", or null when both ends share a clock. */
export function formatZoneShift(span: EventSpan): string | null {
  if (span.zoneShiftMinutes === 0) return null;
  const amount = formatHoursMinutes(Math.abs(span.zoneShiftMinutes));
  const direction = span.zoneShiftMinutes > 0 ? "ahead of" : "behind";
  return `${span.endCity} is ${amount} ${direction} ${span.startCity}`;
}
